/**
 * Mock User Data
 * Contains sample users for all roles in the HALMAS system
 */

import type { User, CompanyUser, ConsultantUser, AuditorUser } from '../types/user';
import { UserRole, UserStatus, getRolePermissions } from '../types/user';
import { OrganizationType } from '../types/organization';

/**
 * Mock Company Admin
 */
export const mockCompanyAdmin: CompanyUser = {
  id: 'user-001',
  email: 'ahmad@nasilemakfood.com.my',
  firstName: 'Ahmad',
  lastName: 'bin Abdullah',
  fullName: 'Ahmad bin Abdullah',

  // Role and organization
  role: UserRole.COMPANY_ADMIN,
  organizationId: 'org-001',
  organizationType: OrganizationType.COMPANY,

  // Contact
  phone: '+60123456789',
  mobile: '+60123456789',
  position: 'Operations Director',

  // Account status
  status: UserStatus.ACTIVE,
  emailVerified: true,
  phoneVerified: true,

  // Permissions
  permissions: getRolePermissions(UserRole.COMPANY_ADMIN),

  // Profile
  profilePicture: undefined,
  language: 'en-US',
  timezone: 'Asia/Kuala_Lumpur',

  // Company-specific
  departmentName: 'Operations',
  isMainContact: true,

  // Metadata
  createdAt: new Date('2025-11-01'),
  updatedAt: new Date('2025-12-20'),
  lastLoginAt: new Date('2025-12-30T08:30:00'),
  lastLoginIp: '192.168.1.100',
};

/**
 * Mock Company Staff
 */
export const mockCompanyStaff: CompanyUser = {
  id: 'user-002',
  email: 'nurul@nasilemakfood.com.my',
  firstName: 'Nurul',
  lastName: 'Aisyah',
  fullName: 'Nurul Aisyah',

  // Role and organization
  role: UserRole.COMPANY_STAFF,
  organizationId: 'org-001',
  organizationType: OrganizationType.COMPANY,

  // Contact
  phone: '+60123456788',
  mobile: '+60123456788',
  position: 'Quality Assurance Officer',

  // Account status
  status: UserStatus.ACTIVE,
  emailVerified: true,
  phoneVerified: true,

  // Permissions
  permissions: getRolePermissions(UserRole.COMPANY_STAFF),

  // Profile
  language: 'en-US',
  timezone: 'Asia/Kuala_Lumpur',

  // Company-specific
  departmentName: 'Quality Assurance',
  isMainContact: false,

  // Metadata
  createdAt: new Date('2025-11-05'),
  updatedAt: new Date('2025-12-15'),
  lastLoginAt: new Date('2025-12-29T14:20:00'),
  lastLoginIp: '192.168.1.101',
};

/**
 * Mock Consultant
 */
export const mockConsultant: ConsultantUser = {
  id: 'user-003',
  email: 'aziz@halalexcellence.com.my',
  firstName: 'Aziz',
  lastName: 'Rahman',
  fullName: 'Aziz Rahman',

  // Role and organization
  role: UserRole.CONSULTANT,
  organizationId: 'org-002',
  organizationType: OrganizationType.CONSULTANCY,

  // Contact
  phone: '+60198765431',
  mobile: '+60198765431',
  position: 'Senior Halal Consultant',

  // Account status
  status: UserStatus.ACTIVE,
  emailVerified: true,
  phoneVerified: true,

  // Permissions
  permissions: getRolePermissions(UserRole.CONSULTANT),

  // Profile
  language: 'en-US',
  timezone: 'Asia/Kuala_Lumpur',

  // Consultant-specific
  certificationNumber: 'HC-CONS-2021-0045',
  certificationExpiryDate: new Date('2026-06-30'),
  specializations: ['Food Manufacturing', 'Food Service', 'Logistics'],
  activeClients: 8,
  completedAudits: 32,

  // Metadata
  createdAt: new Date('2021-07-01'),
  updatedAt: new Date('2025-12-28'),
  lastLoginAt: new Date('2025-12-30T09:15:00'),
  lastLoginIp: '192.168.2.50',
};

/**
 * Mock Consultant Admin
 */
export const mockConsultantAdmin: ConsultantUser = {
  id: 'user-004',
  email: 'siti@halalexcellence.com.my',
  firstName: 'Siti',
  lastName: 'Aminah',
  fullName: 'Dr. Siti Aminah',

  // Role and organization
  role: UserRole.CONSULTANT_ADMIN,
  organizationId: 'org-002',
  organizationType: OrganizationType.CONSULTANCY,

  // Contact
  phone: '+60198765432',
  mobile: '+60198765432',
  position: 'Managing Director',

  // Account status
  status: UserStatus.ACTIVE,
  emailVerified: true,
  phoneVerified: true,

  // Permissions
  permissions: getRolePermissions(UserRole.CONSULTANT_ADMIN),

  // Profile
  language: 'en-US',
  timezone: 'Asia/Kuala_Lumpur',

  // Consultant-specific
  certificationNumber: 'HC-CONS-2018-0012',
  certificationExpiryDate: new Date('2026-12-31'),
  specializations: [
    'Food Manufacturing',
    'Food Service',
    'Logistics',
    'Pharmaceuticals',
  ],
  activeClients: 4,
  completedAudits: 67,

  // Metadata
  createdAt: new Date('2022-01-15'),
  updatedAt: new Date('2025-12-30'),
  lastLoginAt: new Date('2025-12-30T07:45:00'),
  lastLoginIp: '192.168.2.51',
};

/**
 * Mock Auditor
 */
export const mockAuditor: AuditorUser = {
  id: 'user-005',
  email: 'kamal@mais.gov.my',
  firstName: 'Kamal',
  lastName: 'bin Ismail',
  fullName: 'Kamal bin Ismail',

  // Role and organization
  role: UserRole.AUDITOR,
  organizationId: 'org-003',
  organizationType: OrganizationType.CERTIFICATION_BODY,

  // Contact
  phone: '+60355148001',
  mobile: '+60123456701',
  position: 'Halal Auditor',

  // Account status
  status: UserStatus.ACTIVE,
  emailVerified: true,
  phoneVerified: true,

  // Permissions
  permissions: getRolePermissions(UserRole.AUDITOR),

  // Profile
  language: 'en-US',
  timezone: 'Asia/Kuala_Lumpur',

  // Auditor-specific
  auditorLicenseNumber: 'MAIS-AUD-2019-0078',
  licenseExpiryDate: new Date('2026-03-31'),
  accreditationBody: 'JAKIM',
  qualifications: [
    'BSc Food Science',
    'Halal Auditor Certification',
    'ISO 22000 Lead Auditor',
  ],
  certifications: ['MS 1500:2019', 'MHMS 2020', 'ISO 22000:2018'],
  auditsCompleted: 45,
  certificatesIssued: 0, // Regular auditors don't issue certificates

  // Metadata
  createdAt: new Date('2019-04-01'),
  updatedAt: new Date('2025-12-29'),
  lastLoginAt: new Date('2025-12-29T16:30:00'),
  lastLoginIp: '10.0.1.50',
};

/**
 * Mock Lead Auditor
 */
export const mockLeadAuditor: AuditorUser = {
  id: 'user-006',
  email: 'fadzil@mais.gov.my',
  firstName: 'Mohd Fadzil',
  lastName: 'bin Hassan',
  fullName: 'En. Mohd Fadzil bin Hassan',

  // Role and organization
  role: UserRole.LEAD_AUDITOR,
  organizationId: 'org-003',
  organizationType: OrganizationType.CERTIFICATION_BODY,

  // Contact
  phone: '+60355148000',
  mobile: '+60123456700',
  position: 'Head of Halal Certification Division',

  // Account status
  status: UserStatus.ACTIVE,
  emailVerified: true,
  phoneVerified: true,

  // Permissions
  permissions: getRolePermissions(UserRole.LEAD_AUDITOR),

  // Profile
  language: 'en-US',
  timezone: 'Asia/Kuala_Lumpur',

  // Auditor-specific
  auditorLicenseNumber: 'MAIS-LEAD-2015-0012',
  licenseExpiryDate: new Date('2026-12-31'),
  accreditationBody: 'JAKIM',
  qualifications: [
    'MSc Islamic Studies',
    'BSc Food Technology',
    'Lead Auditor Certification',
    'HACCP Certification',
  ],
  certifications: [
    'MS 1500:2019',
    'MHMS 2020',
    'HAS 23000',
    'ISO 22000:2018',
  ],
  auditsCompleted: 128,
  certificatesIssued: 89,

  // Metadata
  createdAt: new Date('2015-01-10'),
  updatedAt: new Date('2025-12-30'),
  lastLoginAt: new Date('2025-12-30T08:00:00'),
  lastLoginIp: '10.0.1.51',
};

/**
 * Mock CB Admin
 */
export const mockCBAdmin: User = {
  id: 'user-007',
  email: 'admin@mais.gov.my',
  firstName: 'Zainab',
  lastName: 'binti Omar',
  fullName: 'Pn. Zainab binti Omar',

  // Role and organization
  role: UserRole.CB_ADMIN,
  organizationId: 'org-003',
  organizationType: OrganizationType.CERTIFICATION_BODY,

  // Contact
  phone: '+60355148002',
  mobile: '+60123456702',
  position: 'Administrative Manager',

  // Account status
  status: UserStatus.ACTIVE,
  emailVerified: true,
  phoneVerified: true,

  // Permissions
  permissions: getRolePermissions(UserRole.CB_ADMIN),

  // Profile
  language: 'en-US',
  timezone: 'Asia/Kuala_Lumpur',

  // Metadata
  createdAt: new Date('2020-03-01'),
  updatedAt: new Date('2025-12-30'),
  lastLoginAt: new Date('2025-12-30T08:15:00'),
  lastLoginIp: '10.0.1.52',
};

/**
 * Mock System Admin
 */
export const mockSystemAdmin: User = {
  id: 'user-008',
  email: 'sysadmin@halmas.gov.my',
  firstName: 'System',
  lastName: 'Administrator',
  fullName: 'System Administrator',

  // Role and organization
  role: UserRole.SYSTEM_ADMIN,
  organizationId: 'org-003', // Associated with MAIS
  organizationType: OrganizationType.CERTIFICATION_BODY,

  // Contact
  phone: '+60355148099',
  mobile: '+60123456799',
  position: 'System Administrator',

  // Account status
  status: UserStatus.ACTIVE,
  emailVerified: true,
  phoneVerified: true,

  // Permissions
  permissions: getRolePermissions(UserRole.SYSTEM_ADMIN),

  // Profile
  language: 'en-US',
  timezone: 'Asia/Kuala_Lumpur',

  // Metadata
  createdAt: new Date('2020-01-01'),
  updatedAt: new Date('2025-12-30'),
  lastLoginAt: new Date('2025-12-30T07:00:00'),
  lastLoginIp: '10.0.1.100',
};

/**
 * All mock users (for user management/lists)
 */
export const mockUsers: User[] = [
  mockCompanyAdmin,
  mockCompanyStaff,
  mockConsultant,
  mockConsultantAdmin,
  mockAuditor,
  mockLeadAuditor,
  mockCBAdmin,
  mockSystemAdmin,
];

/**
 * Mock company users
 */
export const mockCompanyUsers: CompanyUser[] = [mockCompanyAdmin, mockCompanyStaff];

/**
 * Mock consultant users
 */
export const mockConsultantUsers: ConsultantUser[] = [
  mockConsultant,
  mockConsultantAdmin,
];

/**
 * Mock auditor users
 */
export const mockAuditorUsers: AuditorUser[] = [mockAuditor, mockLeadAuditor];

/**
 * Helper to get user by ID
 */
export function getUserById(id: string): User | undefined {
  return mockUsers.find((user) => user.id === id);
}

/**
 * Helper to get user by email
 */
export function getUserByEmail(email: string): User | undefined {
  return mockUsers.find((user) => user.email === email);
}

/**
 * Helper to get users by organization ID
 */
export function getUsersByOrganizationId(organizationId: string): User[] {
  return mockUsers.filter((user) => user.organizationId === organizationId);
}

/**
 * Helper to get users by role
 */
export function getUsersByRole(role: UserRole): User[] {
  return mockUsers.filter((user) => user.role === role);
}
